import {
  FC,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from "react";
import { Form, Button, Modal } from "react-bootstrap";
import * as ReactQuillModule from "react-quill";
import "react-quill/dist/quill.snow.css";
import QuillEditor from "quill";
import { FaClipboardList } from "react-icons/fa";
import { QuillToolbar, modules, formats } from "./QuillToolbar";

type Props = {
  content: string;
  setContent: (content: string) => void;
  setSelectedWordCount: (count: number) => void;
};

export type LeftSectionHandle = {
  getEditor: () => QuillEditor | null;
  replaceText: (index: number, length: number, newText: string) => void;
  updateWordCount: () => void;
  getHTMLContent: () => string;
  getTextContent: () => string;
};

export const LeftSection = forwardRef<LeftSectionHandle, Props>(
  ({ content, setContent, setSelectedWordCount }, ref) => {
    const quillRef = useRef<any>(null);
    const [showError, setShowError] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const [currentSelection, setCurrentSelection] = useState<{
      index: number;
      length: number;
      text: string;
    } | null>(null);

    const getEditor = useCallback(
      () => quillRef.current?.getEditor() || null,
      []
    );

    useImperativeHandle(ref, () => ({
      getEditor,
      replaceText: (index: number, length: number, newText: string) => {
        const editor = getEditor();
        if (editor) {
          editor.deleteText(index, length);
          editor.insertText(index, newText);
        }
      },
      updateWordCount: () => {
        const editor = getEditor();
        if (editor) {
          const text = editor.getText();
          const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
          setWordCount(wordCount);
          setSelectedWordCount(wordCount);
        }
      },
      getHTMLContent: () => quillRef.current?.getEditor().root.innerHTML || "",
      getTextContent: () => quillRef.current?.getEditor().getText() || "",
    }));

    const handleSelectionChange = useCallback(() => {
      const editor = getEditor();
      if (!editor) return;

      const selection = editor.getSelection();
      if (!selection || selection.length === 0) {
        setCurrentSelection(null);
        return;
      }

      const selectedText = editor.getText(selection.index, selection.length);
      setCurrentSelection({
        index: selection.index,
        length: selection.length,
        text: selectedText,
      });
    }, [getEditor]);

    useEffect(() => {
      const editor = getEditor();
      if (!editor) return;

      editor.on("selection-change", handleSelectionChange);
      return () => {
        editor.off("selection-change", handleSelectionChange);
      };
    }, [getEditor, handleSelectionChange]);

    useEffect(() => {
      const editor = getEditor();
      if (editor) {
        const text = editor.getText();
        const words = text.trim().split(/\s+/).filter(Boolean).length;
        setWordCount(words);
        setSelectedWordCount(words);
      }
    }, [content, getEditor, setSelectedWordCount]);

    const ReactQuill = ReactQuillModule.default as unknown as React.ComponentType<any>;

    return (
      <>
        <QuillToolbar setShowError={setShowError} />
        <Form.Group controlId="contentInput" className="mb-1">
          <ReactQuill
            theme="snow"
            ref={quillRef}
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            style={{
              // height: "42rem",
              borderRadius: "4px",
              backgroundColor: "#fff",
            }}
            className="responsiveRichTextbox"
          />
        </Form.Group>
        <div className="d-flex justify-content-end mt-2 mBottom1">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => alert(`Word Count: ${wordCount}`)}
          >
            <FaClipboardList /> {wordCount} Words
          </Button>
        </div>
        <Modal show={showError} onHide={() => setShowError(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Selected Character Must be Within <b>20 to 800</b>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={() => setShowError(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
);
